import React, { createContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getMe, logout as backendLogout } from "@/integrations/backend/auth";
import type { BackendProfile } from "@/integrations/backend/auth";

export interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  onboarding_completed: boolean;
  created_at: string;
  updated_at: string;
  avatar_url: string | null;
  bio: string | null;
  phone: string | null;
  assigned_trainer_id?: string | null;
  status?: string | null;
  legal_document_approval_needed?: boolean;
  debugger_enabled?: boolean;
}

export interface AuthContextType {
  session: null;
  user: Profile | null;
  profile: Profile | null;
  loading: boolean;
  userRoles: string[];
  rolesLoaded: boolean;
  rolesError: boolean;
  rolesFromCache: boolean;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isTrainer: boolean;
  isClient: boolean;
  refetchProfile: () => Promise<void>;
  // bootstrap flags
  authReady: boolean;
  sessionChecked: boolean;
  rolesReady: boolean;
  profileReady: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<null>(null);
  // In this app, `user` is derived from the backend `profile` payload.
  const [user, setUser] = useState<Profile | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [rolesLoaded, setRolesLoaded] = useState<boolean>(false);
  const [rolesError, setRolesError] = useState<boolean>(false);
  const [rolesFromCache, setRolesFromCache] = useState<boolean>(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [rolesReady, setRolesReady] = useState(false);
  const navigate = useNavigate();

  const resolveAvatarUrl = (url: string | null): string | null => {
    if (!url) return null;
    try {
      if (url.startsWith("http://") || url.startsWith("https://")) return url;
      return url;
    } catch {
      return url;
    }
  };

  useEffect(() => {
    console.debug("[auth] provider mounted");
  }, []);

  const log = (...args: any[]) => {
    try {
      const ts = new Date().toISOString();
      // Use console.log to ensure visibility in default browser log levels
      console.log("[auth]", ts, ...args);
    } catch {}
  };

  const logState = (label: string) => {
    try {
      console.log(`[auth] ${label}`, {
        userId: user?.id ?? null,
        loading,
        rolesLoaded,
        roles: userRoles,
        hasProfile: !!profile,
        path: window.location.pathname,
      });
    } catch {}
  };

  // Expose a small debug helper for manual inspection
  useEffect(() => {
    (window as any).__debugAuth = () => logState("manual-dump");
  }, [user, profile, loading, rolesLoaded, userRoles]);

  // Promise timeout helper to prevent indefinite hangs
  const withTimeout = async <T,>(p: Promise<T>, ms: number, label: string): Promise<T> => {
    return await Promise.race([
      p,
      new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`timeout:${label}:${ms}ms`)), ms)),
    ]) as T;
  };

  // Client-side role escalation is removed for correctness; manage roles in DB.

  const refetchProfile = async () => {
    try {
      log("refetch:start");
      const me = await withTimeout(getMe(), 8000, "getMe-refetch");

      // If session went away, clear everything and mark flags as ready
      if (!me) {
        setProfile(null);
        setUser(null);
        setUserRoles([]);
        setRolesLoaded(true);
        setRolesError(false);
        setRolesFromCache(false);
        setRolesReady(true);
        setProfileReady(true);
        try {
          localStorage.setItem("lastRoles", JSON.stringify({ roles: [], ts: Date.now() }));
        } catch {}
        log("refetch:done:no-user");
        return;
      }

      const roles = me.roles || [];
      const bootProfile = (me.profile as BackendProfile | null) as Profile | null;
      const resolvedProfile = bootProfile
        ? ({ ...bootProfile, avatar_url: resolveAvatarUrl(bootProfile.avatar_url) } as Profile)
        : null;

      setProfile(resolvedProfile);
      // Set user to profile for backend session auth (user is derived from profile)
      setUser(resolvedProfile as any);

      if (roles.length === 0) {
        setUserRoles([]);
        setRolesLoaded(true);
        setRolesError(false);
        setRolesFromCache(false);
        try {
          localStorage.setItem("lastRoles", JSON.stringify({ roles: [], ts: Date.now() }));
        } catch {}
      } else {
        setUserRoles(roles);
        setRolesLoaded(true);
        setRolesError(false);
        setRolesFromCache(false);
        try {
          localStorage.setItem("lastRoles", JSON.stringify({ roles, ts: Date.now() }));
        } catch {}
      }

      setProfileReady(true);
      setRolesReady(true);
      log("refetch:done", { rolesCount: roles.length, hasProfile: !!me.profile });
    } catch (e) {
      console.warn("[auth] refetchProfile failed", e);
      // On failure, fall back to cached roles if available, similar to bootstrap
      try {
        const cached = JSON.parse(localStorage.getItem("lastRoles") || "null");
        if (cached?.roles?.length) {
          setUserRoles(cached.roles as string[]);
          setRolesLoaded(true);
          setRolesFromCache(true);
          setRolesError(true);
          log("refetch:fallback:using-cache", { rolesCount: (cached.roles as string[]).length });
        } else {
          setUserRoles([]);
          setRolesLoaded(false);
          setRolesFromCache(false);
          setRolesError(true);
          log("refetch:fallback:no-cache");
        }
      } catch {
        setUserRoles([]);
        setRolesLoaded(false);
        setRolesFromCache(false);
        setRolesError(true);
        log("refetch:fallback:cache-error");
      }
      setRolesReady((prev) => prev || rolesLoaded || rolesFromCache);
      setProfileReady(true);
    }
  };

  const bootstrapInflight = useRef(false);

  useEffect(() => {
    const bootstrap = async () => {
      if (bootstrapInflight.current) {
        log("bootstrap:skip:inflight");
        return;
      }
      bootstrapInflight.current = true;
      setLoading(true);
      try {
        log("bootstrap:start:backend");
        setSession(null);
        setUser(null);
        setSessionChecked(true);

        const me = await withTimeout(getMe(), 8000, "getMe");
        if (!me) {
          setProfile(null);
          setUser(null);
          setUserRoles([]);
          setRolesLoaded(true);
          setRolesReady(true);
          setProfileReady(true);
          return;
        }
        const roles = me.roles || [];
        const bootProfile = (me.profile as BackendProfile | null) as Profile | null;
        const resolvedProfile = bootProfile ? { ...bootProfile, avatar_url: resolveAvatarUrl(bootProfile.avatar_url) } as Profile : null;
        setProfile(resolvedProfile);
        // Set user to profile for backend session auth (user is derived from profile)
        setUser(resolvedProfile as any);

        if (roles.length === 0) {
          setUserRoles([]);
          setRolesLoaded(true);
          setRolesError(false);
          setRolesFromCache(false);
          try {
            localStorage.setItem("lastRoles", JSON.stringify({ roles: [], ts: Date.now() }));
          } catch {}
        } else {
          setUserRoles(roles);
          setRolesLoaded(true);
          setRolesError(false);
          setRolesFromCache(false);
          try {
            localStorage.setItem("lastRoles", JSON.stringify({ roles, ts: Date.now() }));
          } catch {}
        }
        setProfileReady(true);
        setRolesReady(true);
        log("bootstrap:done:backend", { rolesCount: roles.length, hasProfile: !!me.profile });
      } catch (e) {
        console.warn("[auth] backend bootstrap failed", e);
        setProfile(null);
        setUser(null);
        try {
          const cached = JSON.parse(localStorage.getItem("lastRoles") || "null");
          if (cached?.roles?.length) {
            setUserRoles(cached.roles as string[]);
            setRolesLoaded(true);
            setRolesFromCache(true);
            setRolesError(true);
            log("bootstrap:fallback:using-cache", { rolesCount: (cached.roles as string[]).length });
          } else {
            setUserRoles([]);
            setRolesLoaded(false);
            setRolesFromCache(false);
            setRolesError(true);
            log("bootstrap:fallback:no-cache");
          }
        } catch {
          setUserRoles([]);
          setRolesLoaded(false);
          setRolesFromCache(false);
          setRolesError(true);
          log("bootstrap:fallback:cache-error");
        }
        setRolesReady((prev) => prev || rolesLoaded || rolesFromCache);
        setProfileReady(true);
        setSessionChecked(true);
        log("bootstrap:fallback:finalized");
      } finally {
        setLoading(false);
        log("bootstrap:end:backend");
        bootstrapInflight.current = false;
      }
    };

    bootstrap();
    return () => {};
  }, []);

  // Note: Avoid artificial loading timeouts; loading will turn false after
  // profile/roles are fetched to prevent incorrect redirects.

  // Watchdog no longer needed with atomic RPC + guards; keeping minimal protection
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      if (loading) {
        console.warn("[auth] loading took > 8s");
        logState("watchdog-8s");
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [loading]);

  // Log key state transitions to diagnose stuck loading
  useEffect(() => {
    log("state", {
      loading,
      sessionChecked,
      rolesReady,
      profileReady,
      rolesLoaded,
      userId: null,
      roles: userRoles,
      path: window.location.pathname,
    });
  }, [loading, sessionChecked, rolesReady, profileReady, rolesLoaded, userRoles.join("|")]);

  const signOut = async () => {
    setLoading(true);
    try {
      await backendLogout();
      setSession(null);
      setUser(null);
      setProfile(null);
      setUserRoles([]);
      setRolesLoaded(false);
      setRolesReady(false);
      setProfileReady(false);
      navigate("/");
    } catch (error) {
      console.error("Error during sign out:", error);
    } finally {
      setLoading(false);
    }
  };

  // Optional break-glass whitelist during outages
  let whitelistedAdmin = false;
  try {
    const list = (import.meta as any)?.env?.VITE_ADMIN_EMAIL_WHITELIST as string | undefined;
    const emails = (list || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    const email = (user as any)?.email?.toLowerCase?.() as string | undefined;
    whitelistedAdmin = !!(email && emails.includes(email));
  } catch {}

  const isAdmin = userRoles.includes("admin") || (rolesError && whitelistedAdmin);
  const isTrainer = userRoles.includes("trainer");
  const isClient = userRoles.includes("client");
  const authReady = !!user && sessionChecked && rolesReady && profileReady;
  // For backend session auth, treat presence of profile/roles as readiness even if user is null
  const backendReady = sessionChecked && rolesReady && profileReady;

  const contextValue: AuthContextType = {
    session,
    user,
    profile,
    loading,
    userRoles,
    rolesLoaded,
    rolesError,
    rolesFromCache,
    signOut,
    isAdmin,
    isTrainer,
    isClient,
    refetchProfile,
    authReady: authReady || backendReady,
    sessionChecked,
    rolesReady,
    profileReady,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default AuthContext;
